import { createContext, useContext, useState, useCallback } from 'react';
import Toast from './Toast';

const ToastContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Simple API: showSuccess('message')
  const showSuccess = useCallback((message) => addToast(message, 'success'), [addToast]);
  const showError = useCallback((message) => addToast(message, 'error'), [addToast]);
  const showWarning = useCallback((message) => addToast(message, 'warning'), [addToast]);
  const showInfo = useCallback((message) => addToast(message, 'info'), [addToast]);

  // Object API: show({ title, message, type, duration })
  const show = useCallback(({ title, message, type = 'success', duration = 5000 }) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    return addToast(fullMessage, type, duration);
  }, [addToast]);

  // Module-compatible API: success(message, title, options)
  // Matches the signature used by timekeeping/HR useNotification
  const success = useCallback((message, title = null, options = {}) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    return addToast(fullMessage, 'success', options.duration || 4000);
  }, [addToast]);

  const error = useCallback((message, title = null, options = {}) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    return addToast(fullMessage, 'error', options.duration || 6000);
  }, [addToast]);

  const warning = useCallback((message, title = null, options = {}) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    return addToast(fullMessage, 'warning', options.duration || 5000);
  }, [addToast]);

  const info = useCallback((message, title = null, options = {}) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    return addToast(fullMessage, 'info', options.duration || 4000);
  }, [addToast]);

  const value = {
    // Simple API (existing)
    showSuccess, showError, showWarning, showInfo, show,
    // Module-compatible API (success/error/warning/info with title support)
    success, error, warning, info,
    // Low-level
    addToast, removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 space-y-2 z-toast">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
